/**
 * Business Matchmaking — role-adaptive shared directory.
 *
 * Both Prime Suppliers and SBEs land on /matchmaking. This page transparently
 * fetches /api/matchmaking/directory which returns the counter-party role's
 * profiles (Prime → SBEs, SBE → Primes) with per-profile match_score,
 * starred_by_me, they_starred_me, and is_mutual flags. The directory UI is
 * identical for both roles — only the header copy and the "View Profile" href
 * differ.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Star, StarOff, ExternalLink, Filter, Search, Award, ArrowRight, Sparkles } from "lucide-react";

const COPY = {
  prime: {
    hero_overline: "Prime Supplier · Business Matchmaking",
    hero_title: "Discover SBEs aligned with your sourcing needs",
    hero_body:
      "Browse Small Business Enterprises whose capabilities align with your sourcing categories. Star the SBEs you want to meet during the Business Matchmaking window — final schedules are published by Display America after mutual interest is confirmed.",
    directory_label: "Small Business Enterprises",
    empty: "No SBEs have registered for Business Matchmaking yet.",
    profile_href: (id) => `/sbe/${id}`,
  },
  sbe: {
    hero_overline: "Small Business Enterprise · Business Matchmaking",
    hero_title: "Discover Prime Suppliers you want to meet",
    hero_body:
      "Browse Prime Suppliers and star the companies you would like to pitch your capabilities to. Prime Suppliers see who has starred them — mutual interest becomes a scheduled 15-minute meeting.",
    directory_label: "Prime Suppliers",
    empty: "No Prime Suppliers have completed onboarding yet.",
    profile_href: (id) => `/supplier/${id}`,
  },
};

export default function Matchmaking() {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !!user;
  const [state, setState] = useState({
    loading: true,
    error: null,
    callerRole: null,
    counterpartRole: null,
    results: [],
    config: null,
  });
  const [filters, setFilters] = useState({
    search: "",
    industry: "",
    certification: "",
    onlyStarred: false,
    onlyMutual: false,
  });
  const [itinerary, setItinerary] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/matchmaking/me/itinerary").then(({ data }) => setItinerary(data)).catch(() => {});
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const [cfg, dir] = await Promise.all([
          api.get("/matchmaking/config"),
          api.get("/matchmaking/directory", { params: buildQuery(filters) }),
        ]);
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          callerRole: dir.data.caller.role,
          counterpartRole: dir.data.counterpart_role,
          results: dir.data.results || [],
          config: cfg.data.config,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: err?.response?.data?.detail || "Could not load Business Matchmaking." }));
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed, filters.search, filters.industry, filters.certification, filters.onlyStarred, filters.onlyMutual]);

  const shownIndustries = useMemo(() => {
    if (!state.config) return [];
    return state.config.industry_vocab || [];
  }, [state.config]);

  const totalStarred = state.results.filter((r) => r.starred_by_me).length;
  const totalMutual = state.results.filter((r) => r.is_mutual).length;

  if (authLoading) return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;
  if (!isAuthed) return <Navigate to="/login" replace />;

  // Access-denied state — render a neutral notice without any role-specific hero
  // (the T+L Attendee role has no counterpart in Business Matchmaking).
  if (state.error && !state.callerRole) {
    return (
      <Layout>
        <section className="container-summit py-20 max-w-2xl">
          <div className="overline"><span className="accent-rule" />Business Matchmaking</div>
          {itinerary?.tl_meetings?.length > 0 ? (
            <>
              <h1 className="serif text-3xl md:text-4xl mt-4">My Sourcing Meetings</h1>
              <div className="mt-6"><MySchedule itinerary={itinerary} /></div>
              <Link to="/matchmaking/print" className="mt-6 inline-flex text-[11px] uppercase tracking-[0.18em] border border-zinc-300 hover:border-black px-4 h-9 items-center" data-testid="mm-print-my-itinerary-tl">
                Print My Itinerary
              </Link>
            </>
          ) : (
            <>
              <h1 className="serif text-3xl md:text-4xl mt-4">Access restricted</h1>
              <div className="mt-6 border border-zinc-200 p-6 text-zinc-600 text-sm leading-relaxed" data-testid="mm-error">
                {state.error}
              </div>
            </>
          )}
          <Link to="/" className="btn-black mt-8 inline-flex" data-testid="mm-error-home">
            Back to Home
          </Link>
        </section>
      </Layout>
    );
  }

  const role = state.callerRole;
  const copy = COPY[role] || COPY.prime;

  const toggleStar = async (target) => {
    try {
      if (target.starred_by_me) {
        await api.delete(`/matchmaking/interests/${target.id}`);
        toast.message(`Removed interest in ${target.company}.`);
      } else {
        await api.post("/matchmaking/interests", { to_role: state.counterpartRole, to_id: target.id });
        toast.success(`Interest saved: ${target.company}`);
      }
      setState((s) => ({
        ...s,
        results: s.results.map((r) =>
          r.id === target.id
            ? { ...r, starred_by_me: !r.starred_by_me, is_mutual: !r.starred_by_me && r.they_starred_me }
            : r,
        ),
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update interest.");
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="container-summit py-14 md:py-20">
          <div className="overline"><span className="accent-rule" />{copy.hero_overline}</div>
          <h1 className="serif text-4xl md:text-5xl mt-4 max-w-3xl">{copy.hero_title}</h1>
          <p className="mt-4 text-zinc-600 leading-relaxed max-w-2xl">{copy.hero_body}</p>
          {state.config && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm max-w-4xl" data-testid="mm-config-summary">
              <ConfigStat label="Meeting Duration" value={`${state.config.meeting_duration_min} min`} />
              <ConfigStat label="Time Slots" value={state.config.num_time_slots} />
              <ConfigStat label="Formal Slots" value={state.config.num_formal_slots} testId="mm-config-formal" />
              <ConfigStat label={role === "prime" ? "Your Meeting Cap" : "SBE Meeting Cap"}
                          value={role === "prime" ? state.config.max_meetings_prime : state.config.max_meetings_sbe} />
              <ConfigStat label="Matchmaking Tables" value={`${state.config.table_prefix}-1 → ${state.config.table_prefix}-${state.config.num_tables}`} />
            </div>
          )}
        </div>
      </section>

      {/* My published schedule — formal matchmaking + T+L sourcing meetings */}
      {itinerary?.role && (
        <section className="border-b border-zinc-200 bg-[#FBF8F1]/60" data-testid="mm-my-schedule-section">
          <div className="container-summit py-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="overline"><span className="accent-rule" />My Schedule</div>
                <h2 className="serif text-2xl mt-2">My Business Matchmaking Schedule</h2>
              </div>
              {(itinerary.appointments?.length > 0 || itinerary.tl_meetings?.length > 0) && (
                <Link to="/matchmaking/print" className="text-[11px] uppercase tracking-[0.18em] border border-zinc-300 hover:border-black px-4 h-9 inline-flex items-center" data-testid="mm-print-my-itinerary">
                  Print My Itinerary
                </Link>
              )}
            </div>
            {(itinerary.appointments?.length > 0 || itinerary.tl_meetings?.length > 0) ? (
              <>
                <div className="mt-5 max-w-3xl"><MySchedule itinerary={itinerary} /></div>
                <p className="mt-5 text-sm text-zinc-600 max-w-3xl" data-testid="mm-checkin-note">
                  Keep this schedule available when you arrive. Check in at the Business Matchmaking
                  Check-In Table inside the Supplier Showcase at least five minutes before your meeting.
                </p>
              </>
            ) : (
              <p className="mt-5 text-sm text-zinc-600 max-w-3xl" data-testid="mm-empty-note">
                No formal matchmaking meetings are currently scheduled for you. You are invited to
                visit the Supplier Showcase and participate in open networking throughout the event.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Filter bar */}
      <section className="border-b border-zinc-200 bg-white sticky top-16 z-30">
        <div className="container-summit py-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={filters.search}
                   onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                   data-testid="mm-search"
                   placeholder={`Search ${copy.directory_label} — company, keyword…`}
                   className="w-full h-11 pl-9 pr-3 border border-zinc-300 rounded-none focus:outline-none focus:border-black text-sm" />
          </div>

          <select value={filters.industry}
                  onChange={(e) => setFilters((f) => ({ ...f, industry: e.target.value }))}
                  data-testid="mm-filter-industry"
                  className="h-11 px-3 border border-zinc-300 text-sm rounded-none focus:outline-none focus:border-black">
            <option value="">All Industries</option>
            {shownIndustries.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>

          <label className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] cursor-pointer">
            <input type="checkbox" checked={filters.onlyStarred}
                   onChange={(e) => setFilters((f) => ({ ...f, onlyStarred: e.target.checked, onlyMutual: false }))}
                   data-testid="mm-only-starred" />
            My Interests ({totalStarred})
          </label>

          <label className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] cursor-pointer">
            <input type="checkbox" checked={filters.onlyMutual}
                   onChange={(e) => setFilters((f) => ({ ...f, onlyMutual: e.target.checked, onlyStarred: false }))}
                   data-testid="mm-only-mutual" />
            <Sparkles size={12} className="text-[#A39171]" />
            Mutual ({totalMutual})
          </label>
        </div>
      </section>

      {/* Directory */}
      <section className="container-summit py-10">
        {state.loading ? (
          <div className="text-zinc-500 text-sm" data-testid="mm-loading">Loading directory…</div>
        ) : state.error ? (
          <div className="border border-zinc-300 p-8 text-center text-zinc-600" data-testid="mm-error">
            {state.error}
          </div>
        ) : state.results.length === 0 ? (
          <div className="border border-zinc-200 p-10 text-center text-zinc-500" data-testid="mm-empty">
            {copy.empty}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="mm-results">
            {state.results.map((r) => (
              <ProfileCard key={r.id} row={r}
                           href={copy.profile_href(r.id)}
                           onToggleStar={() => toggleStar(r)} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}

function ProfileCard({ row, href, onToggleStar }) {
  return (
    <article className={`group relative border p-5 flex flex-col gap-4 transition-colors ${row.is_mutual ? "border-[#A39171] bg-[#FBF8F1]" : "border-zinc-200 hover:border-black"}`}
             data-testid={`mm-card-${row.id}`}
             data-mutual={row.is_mutual}
             data-starred={row.starred_by_me}>
      {row.is_mutual && (
        <div className="absolute -top-2.5 left-4 bg-[#A39171] text-white text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 flex items-center gap-1">
          <Sparkles size={10} /> Mutual Interest
        </div>
      )}

      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {row.logo_file_id ? (
            <img src={`${BACKEND_URL}/api/files/${row.logo_file_id}`}
                 alt={row.company}
                 className="h-10 w-10 object-contain flex-shrink-0" />
          ) : (
            <div className="h-10 w-10 bg-zinc-100 flex items-center justify-center text-[10px] uppercase tracking-[0.14em] text-zinc-500 flex-shrink-0">
              {(row.company || "?").slice(0, 2)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[15px] font-medium truncate" title={row.company}>{row.company || "—"}</div>
            {(row.contact || row.booth_number) && (
              <div className="text-xs text-zinc-500 truncate">
                {row.contact}{row.contact && row.booth_number ? " · " : ""}{row.booth_number ? `Station ${row.booth_number}` : ""}
              </div>
            )}
          </div>
        </div>

        <button onClick={onToggleStar}
                data-testid={`mm-star-${row.id}`}
                className={`shrink-0 h-9 w-9 border flex items-center justify-center transition-colors ${row.starred_by_me ? "bg-[#A39171] text-white border-[#A39171]" : "border-zinc-300 text-zinc-500 hover:border-black hover:text-black"}`}
                aria-label={row.starred_by_me ? "Remove interest" : "Star for matchmaking"}>
          <Star size={15} fill={row.starred_by_me ? "currentColor" : "none"} />
        </button>
      </header>

      {row.description && (
        <p className="text-[13px] leading-relaxed text-zinc-600 line-clamp-3">{row.description}</p>
      )}

      {row.industries_served?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {row.industries_served.slice(0, 6).map((v) => (
            <span key={v} className="text-[10px] uppercase tracking-[0.14em] border border-zinc-300 px-1.5 py-0.5 text-zinc-600">{v}</span>
          ))}
          {row.industries_served.length > 6 && (
            <span className="text-[10px] text-zinc-400">+{row.industries_served.length - 6}</span>
          )}
        </div>
      )}

      {row.certifications?.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center">
          <Award size={11} className="text-[#A39171]" />
          {row.certifications.slice(0, 3).map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-[0.14em] text-[#7A6A4A]">{c.split(" — ")[0]}</span>
          ))}
          {row.certifications.length > 3 && (
            <span className="text-[10px] text-zinc-400">+{row.certifications.length - 3}</span>
          )}
        </div>
      )}

      <footer className="mt-auto flex items-center justify-between gap-2 pt-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          {row.match_score > 0 && (
            <span className="inline-flex items-center gap-1" data-testid={`mm-score-${row.id}`}>
              <span className="text-[#A39171]">●</span> Match {row.match_score}
            </span>
          )}
          {row.they_starred_me && !row.is_mutual && (
            <span className="inline-flex items-center gap-1 text-[#A39171]">
              <Star size={10} fill="currentColor" /> They starred you
            </span>
          )}
        </div>
        <Link to={href} data-testid={`mm-profile-link-${row.id}`}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-black hover:text-[#7A6A4A]">
          View Profile <ArrowRight size={12} />
        </Link>
      </footer>
    </article>
  );
}

function ConfigStat({ label, value, testId }) {
  return (
    <div className="border-l border-zinc-200 pl-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-lg font-medium mt-1">{value}</div>
    </div>
  );
}

function buildQuery(filters) {
  const q = {};
  if (filters.search) q.search = filters.search;
  if (filters.industry) q.industry = filters.industry;
  if (filters.certification) q.certification = filters.certification;
  if (filters.onlyStarred) q.only_starred = true;
  if (filters.onlyMutual) q.only_mutual = true;
  return q;
}

const fmtET = (iso, opts = {}) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York", ...opts });

export function MySchedule({ itinerary }) {
  const role = itinerary?.role;
  const formal = itinerary?.appointments || [];
  const tlMeetings = itinerary?.tl_meetings || [];
  return (
    <ul className="grid gap-3" data-testid="mm-my-schedule">
      {formal.map((a) => (
        <li key={a.id} className="flex items-baseline justify-between border-b border-zinc-200 pb-3" data-testid={`mm-sched-${a.id}`}>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">{a.round_label || (a.start_iso && `${fmtET(a.start_iso)} – ${fmtET(a.end_iso)} ET`)}</div>
            <div className="text-sm mt-1 font-medium">
              {role === "prime"
                ? (a.sbe?.company || a.sbe?.name || a.sbe_company || a.sbe_name || "Open slot")
                : (a.prime_supplier?.company_name || "Prime Supplier")}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Table M-{a.table_number}</div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#A39171]">Matchmaking</span>
        </li>
      ))}
      {tlMeetings.map((m) => (
        <li key={m.id} className="flex items-baseline justify-between border-b border-zinc-200 pb-3" data-testid={`mm-tl-${m.id}`}>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 tabular-nums">
              {m.start_iso && `${fmtET(m.start_iso)} – ${fmtET(m.end_iso)} ET`}
            </div>
            <div className="text-sm mt-1 font-medium">
              {role === "tl"
                ? (m.prime_supplier?.company_name || m.sbe?.company || m.sbe_name || "Participant")
                : `${m.tl_leader_name || "Travel + Leisure"} · Travel + Leisure Sourcing`}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">{m.location}</div>
            {m.notes && <div className="text-xs text-zinc-600 mt-1">{m.notes}</div>}
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#7A6A4A]">T+L Sourcing</span>
        </li>
      ))}
    </ul>
  );
}
